import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { StyleFunction, StyleLike } from 'ol/style/Style';
import { Feature } from 'ol';
import { connect } from 'react-redux';
import { Vegnettlenke } from '@/domain/vegnett/Vegnettlenke';
import * as selectors from '../../../selectors/selectors';
import { RootState, VegnettStatistics } from '@/state';
import { roadNetLinkToFeature, roadNetStatisticToFeature } from '@/utils/SpatialHelper';
import { lineStyle } from '../style/LineStyle';
import {
    arrowedLineStyle,
    directionalArrowedLineStyle,
    directionalLineStyle,
    featureStyle,
} from '../style/Style';
import React from 'react';
import { MapContext } from '@/state/context/MapContext';

interface StateProps {
    roadNet: Vegnettlenke[];
    roadNetDirection: string;
    roadNetStatistics: VegnettStatistics[];
    visible: boolean;
    zoom: number;
}

type Props = StateProps;

export class VegnettFeatureLayer extends React.Component<Props, Record<string, never>> {
    static contextType = MapContext;
    declare context: React.ContextType<typeof MapContext>;

    private readonly roadNetSource = new VectorSource();
    private readonly roadNetLayer = new VectorLayer<VectorSource>({
        source: this.roadNetSource,
        style: lineStyle as StyleLike,
        visible: false,
        zIndex: 100,
    });
    private layerAttached = false;
    private currentStyle: StyleLike = lineStyle as StyleLike;
    private currentZIndex = 100;

    componentDidMount() {
        this.roadNetLayer.set('name', 'roadnetLayer');
        this.updateFeatures();
        this.updateStyle();
        this.updateZIndex();
        this.roadNetLayer.setVisible(this.props.visible);
        this.syncLayerAttachment();
    }

    componentDidUpdate(prevProps: Props) {
        const roadNetDataChanged =
            prevProps.roadNet !== this.props.roadNet ||
            prevProps.roadNetStatistics !== this.props.roadNetStatistics ||
            (prevProps.roadNetDirection !== this.props.roadNetDirection &&
                (this.props.roadNet?.length ?? 0) > 0);
        const styleChanged =
            roadNetDataChanged ||
            prevProps.roadNetDirection !== this.props.roadNetDirection ||
            prevProps.zoom !== this.props.zoom;

        if (roadNetDataChanged) {
            this.updateFeatures();
            this.updateZIndex();
        }
        if (styleChanged) {
            this.updateStyle();
        }
        if (prevProps.visible !== this.props.visible) {
            this.roadNetLayer.setVisible(this.props.visible);
        }
        this.syncLayerAttachment();
    }

    componentWillUnmount() {
        if (this.layerAttached) {
            this.context.map.removeLayer(this.roadNetLayer);
            this.layerAttached = false;
        }
    }

    private updateFeatures() {
        let features: Feature[] = [];

        if (this.props.roadNet && this.props.roadNet.length > 0) {
            features = this.props.roadNet
                .filter(link => link.geometri && link.geometri.wkt)
                .map(link => roadNetLinkToFeature(link, this.props.roadNetDirection));
        } else if (
            this.props.roadNetStatistics &&
            this.props.roadNetStatistics.length > 0
        ) {
            features = this.props.roadNetStatistics
                .filter(statistic => statistic.omrade.senterpunkt)
                .map(statistic => roadNetStatisticToFeature(statistic));
        }

        if (this.roadNetSource.isEmpty() && features.length === 0) {
            return;
        }

        this.roadNetSource.clear();
        if (features.length > 0) {
            this.roadNetSource.addFeatures(features);
        }
    }

    private updateStyle() {
        let style: StyleLike = lineStyle as StyleLike;

        if (this.props.roadNet && this.props.roadNet.length > 0) {
            style = (
                this.props.roadNetDirection === 'geometri'
                    ? this.props.zoom >= 14
                        ? arrowedLineStyle
                        : lineStyle
                    : this.props.zoom >= 14
                      ? directionalArrowedLineStyle
                      : directionalLineStyle
            ) as StyleLike;
        } else if (
            this.props.roadNetStatistics &&
            this.props.roadNetStatistics.length > 0
        ) {
            style = featureStyle as StyleFunction;
        }

        if (style !== this.currentStyle) {
            this.roadNetLayer.setStyle(style);
            this.currentStyle = style;
        }
    }

    private updateZIndex() {
        const zIndex =
            this.props.roadNetStatistics &&
            this.props.roadNetStatistics.length > 0 &&
            (!this.props.roadNet || this.props.roadNet.length === 0)
                ? 101
                : 100;

        if (zIndex !== this.currentZIndex) {
            this.roadNetLayer.setZIndex(zIndex);
            this.currentZIndex = zIndex;
        }
    }

    private syncLayerAttachment() {
        const hasFeatures = !this.roadNetSource.isEmpty();

        if (hasFeatures && !this.layerAttached) {
            this.context.map.addLayer(this.roadNetLayer);
            this.layerAttached = true;
        } else if (!hasFeatures && this.layerAttached) {
            this.context.map.removeLayer(this.roadNetLayer);
            this.layerAttached = false;
        }
    }

    render() {
        return null;
    }
}

const mapStateToProps = (state: RootState): StateProps => {
    return {
        roadNet: selectors.stateVegnettlenker(state),
        roadNetDirection: selectors.stateVegnettDirection(state),
        roadNetStatistics: selectors.stateVegnettStatistics(state),
        visible: selectors.stateVegnettVisible(state),
        zoom: selectors.stateZoom(state),
    };
};

export const VegnettLayer = connect(mapStateToProps)(VegnettFeatureLayer);
